// ============================================================================
// ClientCard — one scannable roster entry. Avatar + name, a status pill, the
// last-session recency line and (when booked) the next session, plus an inline
// star/favourite toggle. The whole card is a tap-through link to the client's
// detail page; the star button stops propagation so it toggles in place.
// Mobile-first (single column, comfortable tap targets); rises in on mount and
// lifts on hover. Reduced-motion aware via Framer's useReducedMotion.
// ============================================================================
import { motion, useReducedMotion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Avatar, Badge } from "../../components";
import { IconStar, IconClock, IconCalendar, IconChevronRight, IconMessage } from "../../components/icons";
import { useStarClient } from "../../hooks/clients";
import type { Client } from "../../api";
import { cn } from "../../lib/cn";
import { ageFromBirthday, fmtDate, normalizeSex, type ClientActivity } from "./lib";

interface ClientCardProps {
  client: Client;
  activity: ClientActivity;
  /** Unread (client→trainer) messages from this client — the roster badge. */
  unread?: number;
  /** Stagger index for the entrance animation. */
  index: number;
}

export function ClientCard({ client, activity, unread = 0, index }: ClientCardProps) {
  const { t } = useTranslation("clients");
  const { t: tm } = useTranslation("messages");
  const reduce = useReducedMotion();
  const navigate = useNavigate();
  const star = useStarClient();

  const starred = client.starred === 1;
  const isArchived = client.status === "archived";
  const needs = activity.needsFollowUp;

  const age = ageFromBirthday(client.birthday);
  const sex = normalizeSex(client.sex);
  const sexLabel = sex ? t(`sex.${sex}`) : null;

  const lastLabel =
    activity.daysSinceLast == null
      ? t("recency.never")
      : activity.daysSinceLast === 0
        ? t("recency.today")
        : t("recency.daysAgo", { count: activity.daysSinceLast });

  const open = () => navigate(`/app/clients/${client.id}`);

  return (
    <motion.li
      layout={!reduce}
      initial={reduce ? false : { opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      exit={reduce ? { opacity: 0 } : { opacity: 0, y: -8, scale: 0.98 }}
      transition={{
        duration: 0.32,
        delay: reduce ? 0 : Math.min(index * 0.04, 0.28),
        ease: [0.22, 0.61, 0.36, 1],
      }}
    >
      <div
        role="link"
        tabIndex={0}
        onClick={open}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            open();
          }
        }}
        aria-label={client.name}
        className={cn(
          "group flex cursor-pointer items-center gap-4 rounded-lg bg-surface p-4 ring-1 shadow-sm transition-all duration-200 ease-kinetic",
          "hover:-translate-y-1 hover:shadow-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-volt",
          needs ? "ring-coral/25" : "ring-line",
        )}
      >
        <Avatar
          name={client.name}
          size="lg"
          status={isArchived ? "rest" : needs ? "alert" : starred ? "online" : null}
          src={
            client.profile_photo_id != null
              ? `/api/media/${client.profile_photo_id}`
              : undefined
          }
        />

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="truncate font-display text-base font-bold text-body">
              {client.name}
            </span>
            {age != null && (
              <span className="shrink-0 text-xs font-medium tabular-nums text-body-soft">
                {t("card.age", { count: age })}
              </span>
            )}
            {sexLabel && (
              <span className="shrink-0 text-xs text-body-faint">· {sexLabel}</span>
            )}
          </div>

          {/* status + occupation tags */}
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            {isArchived ? (
              <Badge tone="neutral" dot>
                {t("status.archived")}
              </Badge>
            ) : needs ? (
              <Badge tone="danger" dot>
                {t("status.followUp")}
              </Badge>
            ) : (
              <Badge tone="success" dot>
                {t("status.active")}
              </Badge>
            )}
            {client.occupation && (
              <span className="truncate text-xs text-body-soft">
                {client.occupation}
              </span>
            )}
          </div>

          {/* recency / upcoming line */}
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-body-soft">
            <span
              className={cn(
                "inline-flex items-center gap-1",
                needs &&
                  activity.daysSinceLast != null &&
                  activity.daysSinceLast > 10 &&
                  "font-bold text-volt-600",
              )}
            >
              <IconClock width={13} height={13} className="text-body-faint" />
              {lastLabel}
            </span>
            {activity.nextIso && (
              <span className="inline-flex items-center gap-1 font-medium text-ocean-600">
                <IconCalendar width={13} height={13} />
                {t("recency.next", { date: fmtDate(activity.nextIso) })}
              </span>
            )}
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-1">
          {unread > 0 && (
            <span
              className="inline-flex items-center gap-1 rounded-pill bg-coral-soft px-2 py-1 text-coral-600"
              aria-label={tm("unreadFromClientAria", { count: unread, name: client.name })}
            >
              <IconMessage width={14} height={14} />
              <span className="text-[0.7rem] font-extrabold tabular-nums">
                {unread}
              </span>
            </span>
          )}
          <StarButton
            starred={starred}
            name={client.name}
            pending={star.isPending}
            onToggle={() => star.mutate(client.id)}
          />
          <IconChevronRight
            width={20}
            height={20}
            className="text-body-faint transition-transform duration-fast group-hover:translate-x-0.5 group-hover:text-coral"
          />
        </div>
      </div>
    </motion.li>
  );
}

function StarButton({
  starred,
  name,
  pending,
  onToggle,
}: {
  starred: boolean;
  name: string;
  pending: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation("clients");
  const reduce = useReducedMotion();
  return (
    <motion.button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onToggle();
      }}
      disabled={pending}
      aria-pressed={starred}
      aria-label={
        starred ? t("card.unstar", { name }) : t("card.star", { name })
      }
      whileTap={reduce ? undefined : { scale: 0.82 }}
      className={cn(
        "grid h-10 w-10 shrink-0 place-items-center rounded-pill transition-colors",
        starred
          ? "text-amber"
          : "text-body-faint hover:bg-amber-soft hover:text-amber",
      )}
    >
      <motion.span
        key={starred ? "on" : "off"}
        initial={reduce ? false : { scale: 0.6, rotate: starred ? -30 : 0 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{ type: "spring", stiffness: 500, damping: 16 }}
        className="inline-flex"
      >
        <IconStar filled={starred} width={22} height={22} />
      </motion.span>
    </motion.button>
  );
}
